import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import '../../../../../../sass/apps/commerce/forms/stripe.scss';
import { PaymentErrorObject, PaymentProvider } from '../../CheckoutTypes';
import get from '../../../../../helpers/data_layer/get';
import {
  trackWalletPayment,
  trackWalletDisplay,
} from '../../../tracking/event/index';

const site = get('site');

declare global {
  interface Window {
    ApplePaySession: any;
  }
}

export interface WalletButtonProps {
  amount?: string;
  beforeSubmitErrorMessage?: (paymentProvider: PaymentProvider) => string;
  buttonText?: string;
  country?: any;
  currency?: any;
  localisations: {
    [key: string]: string;
  };
  onSuccess: (id: string, subtype?: any, invoice?: any) => any;
}

function WalletButton({
  amount,
  country = site ? site.country : null,
  currency = site ? site.currency : null,
  localisations,
  onSuccess,
}: WalletButtonProps) {
  const stripe = useStripe();
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const [renderButton, setRenderButton] = useState<boolean>(true);
  const [paymentRequest, setPaymentRequest] = useState<object | any>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentCurrency, setPaymentCurrency] = useState<string | any>(null);
  const [paymentType, setPaymentType] = useState<string | any>(null);
  const [trackButtonDisplay, setTrackButtonDisplay] = useState<boolean>(true);
  const label = 'Pay';

  // Set the payment amount
  useEffect(() => {
    if (amount) {
      setPaymentAmount(Math.round(Number(amount) * 100));
    }
  }, [amount, setPaymentAmount]);

  // Reload the button when paymentAmount changes
  // Use timeout to redraw the component after 100ms
  // It would be more elegant to update paymentRequest
  useEffect(() => {
    if (paymentAmount > 0) {
      setRenderButton(false);
      setTimeout(() => {
        setRenderButton(true);
      }, 100);
    }
  }, [paymentAmount, setRenderButton]);

  // Set currency
  useEffect(() => {
    if (currency) {
      setPaymentCurrency(currency.toLowerCase());
    }
  }, [currency, setPaymentCurrency]);

  // Initialise Stripe paymentRequest
  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: country,
        currency: paymentCurrency,
        total: {
          label: label,
          amount: paymentAmount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          const type = result.applePay
            ? 'apple pay'
            : result.googlePay
            ? 'google pay'
            : result.link
            ? 'link pay'
            : null;
          setPaymentType(type);
          setPaymentRequest(pr);
          if (type && trackButtonDisplay) {
            trackWalletDisplay(type);
            setTrackButtonDisplay(false);
          }
        }
      });
    }
  }, [
    country,
    paymentAmount,
    paymentCurrency,
    setPaymentRequest,
    setTrackButtonDisplay,
    stripe,
    trackButtonDisplay,
  ]);

  // Handle the paymentmethod
  useEffect(() => {
    if (stripe && paymentRequest && paymentType) {
      /* eslint-disable consistent-return */
      paymentRequest.on('paymentmethod', async (ev) => {
        const { uuid } = get('customer');

        const res = await axios.post(
          `/api/users/${uuid}/stripe-payment-intents`,
          {
            amount: paymentAmount,
            currency: paymentCurrency,
          },
        );

        // Get the client_secret
        const clientSecret = res.data.data.client_secret;

        // Confirm the card payment
        const {
          paymentIntent,
          error: confirmError,
        } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );

        // Handle the response
        if (confirmError) {
          ev.complete('fail');
        } else if (paymentIntent) {
          ev.complete('success');
          if (paymentIntent.status === 'requires_action') {
            const { error } = await stripe.confirmCardPayment(clientSecret);
            if (error) {
              setErrorMessage(error.message);
            } else {
              trackWalletPayment(paymentType);
              return onSuccess(paymentIntent.id, paymentType);
            }
          } else {
            trackWalletPayment(paymentType);
            return onSuccess(paymentIntent.id, paymentType);
          }
        }
      });
    }
  }, [
    stripe,
    onSuccess,
    paymentRequest,
    paymentAmount,
    paymentCurrency,
    paymentType,
  ]);

  return (
    <>
      {paymentRequest && renderButton ? (
        <div className="wallet-button-wrapper">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  height: '42px',
                },
              },
            }}
          />
        </div>
      ) : null}
    </>
  );
}

export default WalletButton;
